import React, { useEffect, useState } from 'react';
import { Box, Button, CircularProgress, Grid, Link, Typography } from '@mui/material';
import ImageSearchIcon from '@mui/icons-material/ImageSearch';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import { useDropzone } from 'react-dropzone';
import { useDispatch } from 'react-redux';
import CloseIcon from '@mui/icons-material/Close';
import { notifyError } from '../../actions/action-notifications';
import { extractFaData } from '../../services/utils/upload-document-service';

type Props = {
  handleData: (data: any) => void;
  handleReset: () => void;
};

const sx = {
  wrapper: {
    marginBottom: 3,
    paddingBottom: 2,
    paddingTop: 2,
    border: '1px dashed',
    borderRadius: '3px',
  },
  iconWrapper: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    margin: 'auto 0',
    paddingTop: '0 !important',
  },
  textWrapper: { paddingTop: '0 !important' },
  buttonWrapper: { paddingTop: '0 !important', margin: 'auto 0' },
  img: {
    maxHeight: '100px',
  },
};

const IMAGE_MIMES = [
  'image/png',
  'image/jpeg',
  'image/tiff',
  'image/jp2',
  'image/jpeg2000',
  'image/gif',
  'image/webp',
  'image/bmp',
  'image/x-portable-anymap',
  'image/x-portable-pixmap',
  'image/x-portable-graymap',
  'image/x-portable-bitmap',
];

const PDF_MIMES = [
  'application/pdf',
  'application/x-pdf',
  'application/acrobat',
  'applications/vnd.pdf',
  'text/pdf',
  'text/x-pdf',
];

const ACCEPTED_MIMES = [...IMAGE_MIMES, ...PDF_MIMES];

const isPDF = (type: string) => PDF_MIMES.includes(type);
const isImage = (type: string) => IMAGE_MIMES.includes(type);

export const FaOcrInput: React.FC<Props> = (props): React.ReactElement | null => {
  const { handleData, handleReset } = props;
  const [selectedFile, setSelectedFile] = useState<File | null>();
  const [loading, setLoading] = useState(false);
  const [dataURI, setDataURI] = useState<string>();

  const dispatch = useDispatch();
  const { getRootProps, getInputProps } = useDropzone({
    accept: ACCEPTED_MIMES,
    onDropAccepted: (files: File[]) => {
      setSelectedFile(files[0]);
    },
    onDropRejected: () => {
      dispatch(notifyError('There was a problem uploading the selected file.'));
    },
  });
  const reset = () => {
    setSelectedFile(null);
    if (handleReset) handleReset();
  };

  useEffect(() => {
    if (!selectedFile && !loading) return;
    setLoading(true);
    extractFaData(selectedFile).then(async uploadResult => {
      handleData(uploadResult.data);
      setLoading(false);
      setDataURI(URL.createObjectURL(selectedFile as Blob));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFile]);

  return (
    <>
      <Box {...getRootProps({ disabled: loading })}>
        <input {...getInputProps({ disabled: loading })} data-qa-id="fa-ocr-file-form-input" />
        <Grid container spacing={2} sx={sx.wrapper}>
          <Grid item xs={1} sx={sx.iconWrapper}>
            <ImageSearchIcon color="primary" fontSize="large" />
            <Typography color="primary" variant="subtitle1" fontWeight="bold">
              [OCR]
            </Typography>
          </Grid>
          <Grid item xs={8} sx={sx.textWrapper}>
            <Typography variant="body1">
              Drag an image here or select one. Arbor will automatically extract the data from the
              image using <b>Optical Character Recognition (OCR)</b> and populate the corresponding
              fields.
            </Typography>
          </Grid>
          <Grid item xs={3} sx={sx.buttonWrapper}>
            <Button variant="contained">Select a file</Button>
          </Grid>
        </Grid>
      </Box>
      {selectedFile && (
        <Grid container spacing={2} alignItems="center">
          {loading && <CircularProgress color="primary" size={16} />}
          {!loading && isPDF(selectedFile.type) && <PictureAsPdfIcon color="primary" />}
          {!loading && isImage(selectedFile.type) && (
            <img style={sx.img} src={dataURI} alt="Preview" />
          )}
          {!loading && (
            <Typography>
              {selectedFile.name} -{' '}
              <Link component="button" onClick={reset} underline="always">
                <CloseIcon sx={{ position: 'relative', top: '5px' }} /> Clear image and Form
              </Link>
            </Typography>
          )}
        </Grid>
      )}
    </>
  );
};
